import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { StorageService } from './storage.service';
import { UnsavedChangesService } from './unsaved-changes.service';

/**
 * SessionService - Gestione centralizzata della sessione utente
 * 
 * Responsabilità:
 * - Verifica validità sessione (token + expiry)
 * - Notifica componenti quando sessione scade/invalida
 * - Gestione logout completo con cleanup
 */
@Injectable({
  providedIn: 'root'
})
export class SessionService {
  
  // Observable per notificare i componenti quando la sessione viene invalidata
  private sessionInvalidated = new Subject<void>();
  public onSessionInvalidated$ = this.sessionInvalidated.asObservable();
  
  // Flag per evitare invalidazioni multiple simultanee
  private isInvalidating = false;

  constructor(private storageService: StorageService, private unsavedChanges: UnsavedChangesService) {
    console.log('🔐 SessionService initialized');
  }

  /**
   * Verifica se la sessione è valida
   * Controlla: esistenza utente, esistenza token, scadenza token
   */
  isSessionValid(): boolean {
    const user = this.storageService.getUserStorage();
    
    // 1. Utente non esiste
    if (!user) {
      console.log('🔐 Session invalid: No user in storage');
      return false;
    }
    
    // 2. Token non esiste
    if (!user.token) {
      console.log('🔐 Session invalid: No token');
      return false;
    }
    
    // 3. Controlla scadenza token (se JWT)
    const tokenExpiry = this.getTokenExpiry(user.token);
    if (tokenExpiry && tokenExpiry < new Date()) {
      console.log('🔐 Session invalid: Token expired at', tokenExpiry);
      return false;
    }
    
    return true;
  }

  /**
   * Estrae la data di scadenza dal token JWT
   * Ritorna null se il token non è un JWT valido o non ha exp
   */
  getTokenExpiry(token: string): Date | null {
    try {
      // JWT format: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null; // Non è un JWT
      }
      
      // Decodifica payload (base64)
      const payload = JSON.parse(atob(parts[1]));
      
      if (payload.exp) {
        // exp è in secondi, convertiamo in millisecondi
        return new Date(payload.exp * 1000);
      }
      
      return null;
    } catch (error) {
      // Token non decodificabile, assumiamo valido
      return null;
    }
  }

  /**
   * Ritorna i minuti rimanenti prima della scadenza del token
   * Ritorna -1 se non determinabile
   */
  getMinutesUntilExpiry(): number {
    const user = this.storageService.getUserStorage();
    if (!user?.token) return -1;
    
    const expiry = this.getTokenExpiry(user.token);
    if (!expiry) return -1;
    
    const now = new Date();
    const diffMs = expiry.getTime() - now.getTime();
    return Math.floor(diffMs / 60000);
  }

  /**
   * Invalida la sessione completamente
   * - Pulisce storage
   * - Notifica i subscriber
   * - Forza reload per pulire stato Angular
   */
  invalidateSession(reason: string = 'unknown', forceReload: boolean = true): void {
    // Evita invalidazioni multiple
    if (this.isInvalidating) {
      console.log('🔐 Session invalidation already in progress');
      return;
    }
    
    this.isInvalidating = true;
    console.log(`🔐 Invalidating session. Reason: ${reason}`);

    // 1. Pulisci storage
    this.storageService.removeUserStorage();

    // 1b. Se l'invalidazione è dovuta a un 401 (token scaduto/invalido/mancante),
    // lascia un messaggio per la pagina di login. Vive in sessionStorage così
    // sopravvive al window.location reload (removeUserStorage tocca solo localStorage).
    // La login lo legge, lo mostra come banner e lo cancella — pattern gemello di 'app_error'.
    if (reason === 'unauthorized_401') {
      try {
        sessionStorage.setItem('session_ended', JSON.stringify({
          title: 'Session expired',
          message: 'Your session has expired for security reasons. Please sign in again.'
        }));
      } catch (e) {
        console.log('🔐 Could not persist session-ended message:', e);
      }
    }

    // 2. Notifica i subscriber
    this.sessionInvalidated.next();
    
    // 3. Forza reload per pulire completamente lo stato Angular
    if (forceReload) {
      console.log('🔐 Forcing page reload to clear Angular state');
      // Piccolo delay per permettere la navigazione
      setTimeout(() => {
        window.location.href = '/';
      }, 100);
    }
    
    this.isInvalidating = false;
  }

  /**
   * Logout esplicito dell'utente
   * Sempre con force reload per garantire pulizia completa
   * ✏️ 2026-11-02: con modifiche non salvate (es. editor quotazioni, `UnsavedChangesService`) prima si chiede
   * «Stay» / «Save and log out» / «Log out without saving»; «Stay» o un salvataggio non riuscito lasciano tutto
   * com'era e risolvono false. La sessione scaduta (`handleUnauthorized`) non chiede nulla.
   */
  async logout(): Promise<boolean> {
    if (!(await this.unsavedChanges.confirmLogout())) return false;
    this.invalidateSession('user_logout', true);
    return true;
  }

  /**
   * Gestisce errore 401 dall'interceptor
   * Invalida sessione senza loop
   */
  handleUnauthorized(): void {
    if (!this.isInvalidating) {
      this.invalidateSession('unauthorized_401', true);
    }
  }
}
